import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { Toast as ToastData } from '../types/toast';

interface ToastProps {
  toast: ToastData;
  onClose: (id: string) => void;
  darkMode?: boolean;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose, darkMode = false }) => {
  const { id, type, title, message, duration = 5000, action } = toast;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-appines-emerald" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-appines-petal" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-[#c5a043]" />;
      case 'info':
        return <Info className="h-5 w-5 text-appines-sky" />;
      default:
        return <Info className="h-5 w-5 text-appines-sky" />;
    }
  };

  const getBackgroundColor = () => {
    if (darkMode) {
      switch (type) {
        case 'success':
          return 'bg-appines-emerald/10 border-appines-emerald/30';
        case 'error':
          return 'bg-appines-petal/10 border-appines-petal/30';
        case 'warning':
          return 'bg-appines-honey/15 border-appines-honey/30';
        case 'info':
          return 'bg-appines-sky/10 border-appines-sky/30';
        default:
          return 'bg-appines-petrol border-appines-white/20';
      }
    } else {
      switch (type) {
        case 'success':
          return 'bg-appines-glass/30 border-appines-emerald/30';
        case 'error':
          return 'bg-appines-petal/15 border-appines-petal/30';
        case 'warning':
          return 'bg-appines-honey/40 border-appines-honey/50';
        case 'info':
          return 'bg-appines-sky/20 border-appines-sky/40';
        default:
          return 'bg-appines-white border-[rgba(1,42,54,0.12)]';
      }
    }
  };

  return (
    <div
      className={`max-w-sm w-full rounded-lg border p-4 shadow-lg transition-all duration-300 ease-in-out transform ${getBackgroundColor()}`}
      role="alert"
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h4 className={`text-sm font-medium ${
            darkMode ? 'text-appines-white' : 'text-appines-black'
          }`}>
            {title}
          </h4>
          {message && (
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'
            }`}>
              {message}
            </p>
          )}
          {action && (
            <div className="mt-3">
              <button
                onClick={action.onClick}
                className="text-sm font-medium underline hover:no-underline text-appines-petrol"
              >
                {action.label}
              </button>
            </div>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={() => onClose(id)}
            className={`rounded-md inline-flex transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-appines-emerald ${
              darkMode ? 'text-appines-white/45 hover:text-appines-white' : 'text-appines-petrol/45 hover:text-appines-petrol'
            }`}
          >
            <span className="sr-only">Fermer</span>
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
